import React from 'react';
import { Button, Drawer, Empty, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ReceptionistCallDisplay, formatReceptionistCallDuration } from '../types/ReceptionistCallDisplay';
import useReceptionistCallTranscriptController from '../controllers/useReceptionistCallTranscriptController';

interface ReceptionistCallTranscriptDrawerProps {
  call: ReceptionistCallDisplay;
  open: boolean;
  onClose: () => void;
}

const ReceptionistCallTranscriptDrawer: React.FC<ReceptionistCallTranscriptDrawerProps> = ({ call, open, onClose }) => {
  const { state } = useReceptionistCallTranscriptController(call.id, open);
  const userFirstName = call.userName.split(' ')[0];
  const assistantLabel = userFirstName ? `${call.assistantName} · ${userFirstName}` : call.assistantName;

  return (
    <Drawer
      title={
        <div>
          <div className="receptionist-calls__transcript-title">Transcription</div>
          <div className="receptionist-calls__transcript-subtitle">
            {call.callerName} · {dayjs(call.created).format('D MMM [à] HH:mm')} · {formatReceptionistCallDuration(call.durationMs)}
          </div>
        </div>
      }
      open={open}
      onClose={onClose}
      width={460}
      closable={false}
      extra={<Button type="text" icon={<CloseOutlined />} onClick={onClose} />}
      className="receptionist-calls__transcript-drawer"
    >
      <div className="receptionist-calls__transcript-owner">
        {call.assistantName} · Réceptionniste de <strong>{call.userName || 'Non assigné'}</strong>
      </div>
      {state.isLoading ? (
        <div className="receptionist-calls__transcript-loading">
          <Spin />
        </div>
      ) : state.hasFailed ? (
        <Empty description="Impossible de charger la transcription. Fermez et rouvrez pour réessayer." />
      ) : state.messages.length === 0 ? (
        <Empty description="Aucune transcription disponible" />
      ) : (
        <div className="receptionist-calls__transcript">
          {state.messages.map((message, index) => (
            <div
              key={index}
              className={classNames('receptionist-calls__bubble', {
                'receptionist-calls__bubble--assistant': message.isAssistant,
              })}
            >
              <div className="receptionist-calls__bubble-head">
                {message.isAssistant ? (
                  <span className="receptionist-calls__tag receptionist-calls__tag--motif">{assistantLabel}</span>
                ) : (
                  <span className="receptionist-calls__bubble-speaker">{call.callerName}</span>
                )}
                <span className="receptionist-calls__bubble-time">{message.time}</span>
              </div>
              <div className="receptionist-calls__bubble-text">{message.text}</div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
};

export default ReceptionistCallTranscriptDrawer;
